import { connect, ConnectedProps } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, Dialog, Button, Field, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { HierarchyLevelType } from 'src/enums/hierarchy';
import { createHierarchyItem, updateHierarchyItem } from 'store/entities/hierarchy/hierarchy';
import { selectDesigns } from 'store/entities/hierarchy/selectors';
import { Input } from 'client/common/form-inputs';
import translations from 'src/internationalisation/translation-map.json';
import { getResolver } from 'src/validation/resolver';
import { designsSchemaValidator } from 'schemas/ajv-validators';
import { IDesign, IWellbore } from 'client/common/interfaces/hierarchy.interfaces.ts';
import type { TRootState } from 'src/store/store-types';

type DesignModalProps = PropsFromRedux & {
  companyId: string;
  activeWellbore: IWellbore | null;
  isAdding: boolean;
  editItem: IDesign | null;
  setEditItem: (data: IDesign | null) => void;
  itemModalVisibleUpdated: (data: { type: HierarchyLevelType; value: boolean }) => void;
};

const DesignModal = ({
  companyId,
  itemModalVisibleUpdated,
  createHierarchyItem,
  updateHierarchyItem,
  activeWellbore,
  editItem,
  setEditItem,
  isAdding,
  designs,
}: DesignModalProps) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      wellbore: activeWellbore?.name,
      wellboreid: activeWellbore?.wellboreid,
      name: editItem?.name || '',
      designid: editItem?.designid || null,
    },
    mode: 'onChange',
    resolver: async (data) => {
      const result = await getResolver(designsSchemaValidator)(data);
      const normalizedName = data.name.trim().toLowerCase();
      const hasDuplicateName = designs.some(
        (design: IDesign) =>
          design.wellboreid === activeWellbore?.wellboreid &&
          design.designid !== editItem?.designid &&
          design.name.trim().toLowerCase() === normalizedName,
      );

      if (hasDuplicateName) {
        return {
          values: {},
          errors: {
            ...result.errors,
            name: {
              type: 'validate',
              message: t(translations.projects_duplicateNameError),
            },
          },
        };
      }

      return result;
    },
  });
  console.log({ isAdding });

  const onSubmit = handleSubmit((data) => {
    delete data.wellbore;
    if (editItem) {
      updateHierarchyItem(companyId, HierarchyLevelType.Design, data.designid, {
        ...editItem,
        ...data,
      });
    } else {
      createHierarchyItem(companyId, HierarchyLevelType.Design, {
        name: data.name,
        wellboreid: activeWellbore?.wellboreid,
      });
    }

    setEditItem(null);
    itemModalVisibleUpdated({ type: HierarchyLevelType.Design, value: false });
  });
  const onClose = () => {
    setEditItem(null);
    itemModalVisibleUpdated({ type: HierarchyLevelType.Design, value: false });
  };

  return (
    <Modal visible centered>
      <Dialog
        dialog={{
          heading: editItem ? t(translations.projects_editDesign) : t(translations.projects_addNewDesign),
          width: '600px',
          content: (
            <form>
              <Field label={t(translations.wellbore)} labelLeft labelWidth="80px">
                <Input name="wellbore" control={control} errors={errors} disabled={true} />
              </Field>
              <Field label={t(translations.design)} labelLeft labelWidth="80px">
                <Input name="name" control={control} errors={errors} disabled={isAdding} />
              </Field>
            </form>
          ),
          footer: (
            <>
              <Button
                label={t(translations.done)}
                colored
                onClick={onSubmit}
                loading={isAdding}
                disabled={isAdding || !isValid}
              />
              <Button label={t(translations.cancel)} onClick={onClose} disabled={isAdding} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapDispatchToProps = { createHierarchyItem, updateHierarchyItem };

const mapStateToProps = ({ entities }: TRootState) => ({
  designs: selectDesigns(entities),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(DesignModal), {
  style: { position: 'absolute', bottom: 0, zIndex: 1000 },
});

export { Container as DesignModal };
